import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Stack,
  IconButton,
  CircularProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Menu,
  MenuItem,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  CreditCard as RefundIcon,
  TableRows as TableRowsIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/pt-br';
import { bookingsStore } from '../stores/AdminStore';
import EmployeeAvatarFilter from '../components/EmployeeAvatarFilter';
import WeekNavigation from '../components/WeekNavigation';
import BookingCard from '../components/BookingCard';
import BookingsTable from '../components/BookingsTable';

// Set moment locale to Portuguese (Brazil)
moment.locale('pt-br');

// Auto-refresh interval in milliseconds (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;

const BookingsManagement = observer(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef(null);
  const isTabVisible = useRef(true);

  // View mode state
  const [viewMode, setViewMode] = useState('cards');
  const effectiveViewMode = isMobile ? 'cards' : viewMode;

  // Table actions menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Cancel booking dialog state
  const [cancelDialog, setCancelDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [processRefund, setProcessRefund] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Initialize state from URL params on mount
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const employeeParam = searchParams.get('employee');

    // Restore selected date from URL or use today
    if (dateParam) {
      const parsedDate = moment(dateParam, 'YYYY-MM-DD');
      if (parsedDate.isValid()) {
        bookingsStore.setSelectedDate(parsedDate);
        // Set week to include this date
        bookingsStore.setWeekStart(parsedDate.clone().startOf('week'));
      }
    }

    // Restore employee filter from URL
    if (employeeParam) {
      bookingsStore.setSelectedEmployee(employeeParam);
    }

    // Load initial data
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Setup auto-refresh and page visibility handling
  useEffect(() => {
    // Handle page visibility changes
    const handleVisibilityChange = () => {
      isTabVisible.current = !document.hidden;

      if (isTabVisible.current) {
        // Tab became visible - refresh immediately and restart interval
        loadData();
        startAutoRefresh();
      } else {
        // Tab hidden - stop auto-refresh
        stopAutoRefresh();
      }
    };

    // Start auto-refresh
    startAutoRefresh();

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      stopAutoRefresh();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    bookingsStore.loadEmployees();
    bookingsStore.loadWeekBookingCounts();
    await bookingsStore.loadBookingsWithFilters();
    setLastUpdated(new Date());
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
  };

  const startAutoRefresh = () => {
    // Clear any existing interval
    stopAutoRefresh();

    // Set up new interval
    intervalRef.current = setInterval(() => {
      if (isTabVisible.current) {
        loadData();
      }
    }, AUTO_REFRESH_INTERVAL);
  };

  const stopAutoRefresh = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Sync URL params when selected date or employee changes
  useEffect(() => {
    if (!bookingsStore.selectedDate) return;

    const params = new URLSearchParams(searchParams);
    params.set('date', bookingsStore.selectedDate.format('YYYY-MM-DD'));

    if (bookingsStore.selectedEmployee && bookingsStore.selectedEmployee !== 'all') {
      params.set('employee', bookingsStore.selectedEmployee);
    } else {
      params.delete('employee');
    }

    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingsStore.selectedDate, bookingsStore.selectedEmployee]);

  const handleDateSelect = (day) => {
    bookingsStore.setSelectedDate(day);
  };

  const handleEmployeeChange = (employeeId) => {
    bookingsStore.setSelectedEmployee(employeeId);
  };

  const handlePreviousWeek = () => {
    bookingsStore.previousWeek();
  };

  const handleNextWeek = () => {
    bookingsStore.nextWeek();
  };

  const handleGoToToday = () => {
    const today = moment();
    bookingsStore.setSelectedDate(today);
    bookingsStore.setWeekStart(today.clone().startOf('week'));
  };

  // Action handlers - delegate to store
  const handleAction = async (action, booking) => {
    switch (action) {
      case 'confirm':
        const confirmSuccess = await bookingsStore.confirmBooking(booking.id || booking._id);
        if (confirmSuccess) {
          setSnackbarOpen(true);
        }
        break;
      case 'cancel':
        handleCancelClick(booking);
        break;
      case 'refund':
        handleRefundClick(booking);
        break;
      default:
        console.warn('Unknown action:', action);
    }
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleCancelClick = (booking) => {
    handleMenuClose();
    setSelectedBooking(booking);
    setProcessRefund(false); // Default to not refunding
    setCancelDialog(true);
  };

  const handleRefundClick = (booking) => {
    handleMenuClose();
    setSelectedBooking(booking);
    setProcessRefund(true); // Auto-enable refund for direct refund action
    setCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialog(false);
    setSelectedBooking(null);
    setProcessRefund(false);
  };

  const handleConfirmCancel = async () => {
    if (!selectedBooking) return;

    const success = await bookingsStore.cancelBooking(selectedBooking.id || selectedBooking._id, processRefund);
    if (success) {
      handleCloseCancelDialog();
      setSnackbarOpen(true);
    }
  };


  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">
            Agendamentos
          </Typography>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Última atualização: {moment(lastUpdated).fromNow()}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {/* View Mode Toggle - hide on mobile */}
          {!isMobile && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="table">
                <TableRowsIcon />
              </ToggleButton>
              <ToggleButton value="cards">
                <ViewModuleIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Tooltip title="Atualizar agendamentos">
            <IconButton
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              color="primary"
              size="large"
            >
              {isRefreshing ? (
                <CircularProgress size={24} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {bookingsStore.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {bookingsStore.error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Stack spacing={{ xs: 1, md: 2 }}>
          {/* Week navigation */}
          <WeekNavigation
            weekDays={bookingsStore.weekDays}
            selectedDate={bookingsStore.selectedDate}
            bookingCountsByDate={bookingsStore.bookingCountsByDate}
            onDateSelect={handleDateSelect}
            onPreviousWeek={handlePreviousWeek}
            onNextWeek={handleNextWeek}
            onGoToToday={handleGoToToday}
          />

          {/* Employee filter */}
          <EmployeeAvatarFilter
            employees={bookingsStore.employees}
            selectedEmployee={bookingsStore.selectedEmployee}
            onEmployeeChange={handleEmployeeChange}
          />
        </Stack>
      </Paper>

      {/* Table or Card View */}
      {effectiveViewMode === 'table' ? (
        <BookingsTable
          bookings={bookingsStore.bookings}
          loading={bookingsStore.loading}
          onMenuOpen={handleMenuOpen}
        />
      ) : (
        bookingsStore.bookings.length === 0 ? (
          <Paper
            sx={{
              p: 8,
              textAlign: 'center',
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'grey.300'
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum agendamento encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Não há agendamentos para {bookingsStore.selectedDate.format('ddd, D [de] MMMM [de] YYYY')}
              {bookingsStore.selectedEmployeeName && (
                <>
                  {' e filtro: '}
                  <strong>{bookingsStore.selectedEmployeeName}</strong>
                </>
              )}
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr'
              },
              gap: 2
            }}
          >
            {bookingsStore.bookings.map((booking) => (
              <BookingCard
                key={booking.id || booking._id}
                booking={booking}
                onAction={handleAction}
              />
            ))}
          </Box>
        )
      )}

      {/* Table Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedRow && (
          <>
            {selectedRow.status === 'hold' && (
              <MenuItem onClick={() => { handleAction('confirm', selectedRow); handleMenuClose(); }}>
                <CheckIcon sx={{ mr: 1, fontSize: 20 }} /> Confirmar
              </MenuItem>
            )}
            {selectedRow.status === 'hold' && <Divider />}
            <MenuItem
              onClick={() => { handleAction('cancel', selectedRow); }}
              sx={{ color: 'error.main' }}
            >
              <CancelIcon sx={{ mr: 1, fontSize: 20 }} /> Cancelar
            </MenuItem>
            {selectedRow.payment?.status === 'completed' && (
              <MenuItem
                onClick={() => { handleAction('refund', selectedRow); }}
                sx={{ color: 'warning.main' }}
              >
                <RefundIcon sx={{ mr: 1, fontSize: 20 }} /> Reembolsar
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* Cancel Booking Dialog */}
      <Dialog open={cancelDialog} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar Agendamento</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Serviço:</strong> {selectedBooking.service?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Cliente:</strong> {selectedBooking.customer?.name || 'N/A'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Data:</strong> {new Date(selectedBooking.startAt).toLocaleString('pt-BR')}
              </Typography>
              {selectedBooking.payment?.amount && (
                <Typography variant="body2" gutterBottom>
                  <strong>Valor:</strong> {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(selectedBooking.payment.amount)}
                </Typography>
              )}
              {selectedBooking.payment?.status && (
                <Typography variant="body2" gutterBottom>
                  <strong>Status do Pagamento:</strong>{' '}
                  {selectedBooking.payment.status === 'completed' ? 'Pago' :
                   selectedBooking.payment.status === 'pending' ? 'Pendente' :
                   selectedBooking.payment.status}
                </Typography>
              )}

              <Box sx={{ mt: 3, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={processRefund}
                      onChange={(e) => setProcessRefund(e.target.checked)}
                      disabled={selectedBooking.payment?.status !== 'completed'}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Processar reembolso</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedBooking.payment?.status === 'completed'
                          ? 'O valor será devolvido ao cliente'
                          : 'Reembolso disponível apenas para pagamentos concluídos'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>
            Manter Agendamento
          </Button>
          <Button onClick={handleConfirmCancel} variant="contained" color="error">
            Confirmar Cancelamento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => {
          setSnackbarOpen(false);
          bookingsStore.setSuccess(null);
        }}
        message={bookingsStore.successMessage}
      />
    </Box>
  );
});

export default BookingsManagement;
